import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  MONTH_NAMES,
  addDays,
  addMonths,
  daysInMonth,
  formatDisplay,
  formatLong,
  istNowTime,
  istToday,
  parseInput,
  to12Hour,
  to24Hour,
  weekday,
  type Meridiem,
} from './datePickerFormat';
import styles from './DatePicker.module.css';

interface Props {
  /** yyyy-MM-dd, or yyyy-MM-ddTHH:mm when withTime; '' when empty. */
  value: string;
  onChange: (value: string) => void;
  withTime?: boolean;
  id?: string;
  'aria-label'?: string;
  /** A caller's own field error (e.g. "Pick a date"); merged with the picker's typing hint. */
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MERIDIEMS: Meridiem[] = ['AM', 'PM'];
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

/**
 * Shared IST date / date-time field. Date only: picking a day finishes. Date and time: pick a
 * day and time, then Done. Emits the same strings a native date / datetime-local input does.
 */
export function DatePicker({
  value,
  onChange,
  withTime = false,
  id,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  min,
  max,
  required,
  disabled,
  readOnly,
  className,
}: Props) {
  const { t } = useTranslation();
  const uid = useId();
  const hintId = `${uid}-hint`;
  const headingId = `${uid}-heading`;
  const [text, setText] = useState(() => formatDisplay(value, withTime));
  const [hint, setHint] = useState('');
  const [open, setOpen] = useState(false);
  const [focusDay, setFocusDay] = useState('');
  const [draftDay, setDraftDay] = useState('');
  const [hour, setHour] = useState('00');
  const [minute, setMinute] = useState('00');
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const emitted = useRef(value);
  const wantGridFocus = useRef(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Show values set from outside (defaults, resets) in display format.
  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setText(formatDisplay(value, withTime));
      setHint('');
    }
  }, [value, withTime]);

  const emit = (next: string) => {
    emitted.current = next;
    if (next !== value) onChange(next);
  };

  const rangeHint = (next: string): string => {
    if (min && next.slice(0, min.length) < min) {
      return `Pick a ${min.length > 10 ? 'time' : 'date'} on or after ${formatDisplay(min, min.length > 10)}`;
    }
    if (max && next.slice(0, max.length) > max) {
      return `Pick a ${max.length > 10 ? 'time' : 'date'} on or before ${formatDisplay(max, max.length > 10)}`;
    }
    return '';
  };

  const dayDisabled = (day: string) =>
    Boolean((min && day < min.slice(0, 10)) || (max && day > max.slice(0, 10)));

  const handleType = (next: string) => {
    setText(next);
    if (next.trim() === '') {
      setHint('');
      emit('');
      return;
    }
    const parsed = parseInput(next, withTime);
    if (!parsed) {
      setHint(
        next.trim().length >= 8
          ? withTime
            ? 'Enter a date and time like 17 Oct 2026, 2:30 PM'
            : 'Enter a date like 17 Oct 2026'
          : '',
      );
      return;
    }
    const outside = rangeHint(parsed);
    setHint(outside);
    if (!outside) emit(parsed);
  };

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) inputRef.current?.focus();
  }, []);

  const openPopover = () => {
    if (disabled || readOnly || open) return;
    const today = istToday();
    let start = value ? value.slice(0, 10) : today;
    if (!value && dayDisabled(start))
      start = min && start < min.slice(0, 10) ? min.slice(0, 10) : (max ?? start).slice(0, 10);
    setFocusDay(start);
    setDraftDay(value ? value.slice(0, 10) : '');
    const [h, m] = (value.length > 10 ? value.slice(11, 16) : istNowTime()).split(':');
    setHour(h);
    setMinute(m);
    setText(formatDisplay(value, withTime));
    setHint('');
    wantGridFocus.current = true;
    setOpen(true);
  };

  const finish = (next: string) => {
    emit(next);
    setText(formatDisplay(next, withTime));
    setHint('');
    close(true);
  };

  const selectDay = (day: string) => {
    if (dayDisabled(day)) return;
    if (withTime) {
      setDraftDay(day);
      setFocusDay(day);
    } else {
      finish(day);
    }
  };

  const moveFocus = (day: string) => {
    wantGridFocus.current = true;
    setFocusDay(day);
  };

  const onDayKey = (event: KeyboardEvent<HTMLButtonElement>, day: string) => {
    const moves: Record<string, () => string> = {
      ArrowLeft: () => addDays(day, -1),
      ArrowRight: () => addDays(day, 1),
      ArrowUp: () => addDays(day, -7),
      ArrowDown: () => addDays(day, 7),
      PageUp: () => addMonths(day, -1),
      PageDown: () => addMonths(day, 1),
      Home: () => addDays(day, -weekday(day)),
      End: () => addDays(day, 6 - weekday(day)),
    };
    if (moves[event.key]) {
      event.preventDefault();
      moveFocus(moves[event.key]());
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectDay(day);
    }
  };

  const onInputKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (open && event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
      return;
    }
    if (!open && (event.key === 'Enter' || event.key === 'ArrowDown')) {
      event.preventDefault();
      openPopover();
    }
  };

  const onPopoverKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close(true);
    }
  };

  // Focus the active day when the popover opens or the keyboard moves.
  useEffect(() => {
    if (!open || !wantGridFocus.current) return;
    wantGridFocus.current = false;
    popoverRef.current?.querySelector<HTMLButtonElement>(`[data-day="${focusDay}"]`)?.focus();
  }, [open, focusDay]);

  // Close (discarding any draft) on a click outside the field and the popover.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Fixed position under the field (or above it when there is no room), so dialog overflow never clips it.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const field = wrapperRef.current?.getBoundingClientRect();
      const pop = popoverRef.current;
      if (!field || !pop) return;
      const height = pop.offsetHeight;
      const width = pop.offsetWidth;
      const below = window.innerHeight - field.bottom;
      const top =
        below < height + 12 && field.top > below
          ? Math.max(8, field.top - height - 4)
          : field.bottom + 4;
      const left = Math.max(8, Math.min(field.left, window.innerWidth - width - 8));
      setPos((prev) => (prev.top === top && prev.left === left ? prev : { top, left }));
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, focusDay, draftDay]);

  const today = istToday();
  const viewYear = Number(focusDay.slice(0, 4));
  const viewMonth = Number(focusDay.slice(5, 7));
  const selectedDay = withTime ? draftDay : value.slice(0, 10);
  const draft = draftDay ? `${draftDay}T${hour}:${minute}` : '';
  const clock = to12Hour(hour);
  const draftHint = withTime && open ? (draft ? rangeHint(draft) : 'Pick a day') : '';

  const weeks: Array<Array<string | null>> = [];
  if (open) {
    const first = `${focusDay.slice(0, 7)}-01`;
    const cells: Array<string | null> = Array.from({ length: weekday(first) }, () => null);
    for (let d = 0; d < daysInMonth(viewYear, viewMonth); d += 1) cells.push(addDays(first, d));
    while (cells.length % 7) cells.push(null);
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  }

  const popover = open
    ? createPortal(
        <div
          ref={popoverRef}
          role="dialog"
          aria-labelledby={headingId}
          className={styles.popover}
          style={{ top: pos.top, left: pos.left }}
          onKeyDown={onPopoverKey}
        >
          <div className={styles.header}>
            <button
              type="button"
              className={styles.nav}
              aria-label={t('ui.datePicker.previousMonth')}
              onClick={() => setFocusDay(addMonths(focusDay, -1))}
            >
              <Chevron direction="left" />
            </button>
            <span id={headingId} className={styles.heading} aria-live="polite">
              {MONTH_NAMES[viewMonth - 1]} {viewYear}
            </span>
            <button
              type="button"
              className={styles.nav}
              aria-label={t('ui.datePicker.nextMonth')}
              onClick={() => setFocusDay(addMonths(focusDay, 1))}
            >
              <Chevron direction="right" />
            </button>
          </div>
          <table role="grid" aria-labelledby={headingId} className={styles.grid}>
            <thead>
              <tr>
                {WEEKDAYS.map((name) => (
                  <th key={name} scope="col" className={styles.weekday}>
                    {name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, row) => (
                <tr key={row}>
                  {week.map((day, col) =>
                    day ? (
                      <td key={day} role="gridcell" aria-selected={day === selectedDay}>
                        <button
                          type="button"
                          data-day={day}
                          tabIndex={day === focusDay ? 0 : -1}
                          aria-label={formatLong(day)}
                          aria-disabled={dayDisabled(day) || undefined}
                          aria-current={day === today ? 'date' : undefined}
                          className={[
                            styles.day,
                            day === today ? styles.today : '',
                            day === selectedDay ? styles.selected : '',
                          ].join(' ')}
                          onClick={() => selectDay(day)}
                          onKeyDown={(event) => onDayKey(event, day)}
                        >
                          {Number(day.slice(8, 10))}
                        </button>
                      </td>
                    ) : (
                      <td key={`blank-${col}`} role="gridcell" />
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {withTime && (
            <div className={styles.footer}>
              <div className={styles.time}>
                <select
                  aria-label={t('ui.datePicker.hour')}
                  value={clock.hour}
                  onChange={(e) => setHour(to24Hour(e.target.value, clock.meridiem))}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
                <span aria-hidden="true">:</span>
                <select
                  aria-label={t('ui.datePicker.minute')}
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                >
                  {MINUTES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={t('ui.datePicker.meridiem')}
                  value={clock.meridiem}
                  onChange={(e) => setHour(to24Hour(clock.hour, e.target.value as Meridiem))}
                >
                  {MERIDIEMS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <span className={styles.zone}>{t('ui.datePicker.timeZone')}</span>
              </div>
              <button
                type="button"
                className={styles.done}
                disabled={Boolean(draftHint)}
                onClick={() => finish(draft)}
              >
                {t('ui.datePicker.done')}
              </button>
              {draftHint && <span className={styles.draftHint}>{draftHint}</span>}
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <span className={styles.root}>
      <span ref={wrapperRef} className={styles.control}>
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="text"
          autoComplete="off"
          aria-label={ariaLabel}
          aria-invalid={hint || ariaInvalid ? true : undefined}
          aria-describedby={[hint ? hintId : null, ariaDescribedBy].filter(Boolean).join(' ') || undefined}
          placeholder={withTime ? 'DD Mon YYYY, h:mm AM/PM' : 'DD Mon YYYY'}
          className={[className, styles.input].filter(Boolean).join(' ')}
          value={text}
          min={min}
          max={max}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(event) => handleType(event.target.value)}
          onKeyDown={onInputKey}
          onBlur={() => {
            if (open) return;
            setText(formatDisplay(value, withTime));
            setHint('');
          }}
        />
        {!readOnly && (
          <button
            type="button"
            className={styles.trigger}
            aria-label={t('ui.datePicker.openCalendar')}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={disabled}
            onClick={() => (open ? close(true) : openPopover())}
          >
            <CalendarIcon />
          </button>
        )}
      </span>
      {hint && (
        <span id={hintId} className={styles.hint} aria-live="polite">
          {hint}
        </span>
      )}
      {popover}
    </span>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm13 8H4v9a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-9ZM8 13a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
      />
    </svg>
  );
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d={
          direction === 'left'
            ? 'M14.7 6.3a1 1 0 0 1 0 1.4L10.4 12l4.3 4.3a1 1 0 0 1-1.4 1.4l-5-5a1 1 0 0 1 0-1.4l5-5a1 1 0 0 1 1.4 0Z'
            : 'M9.3 6.3a1 1 0 0 1 1.4 0l5 5a1 1 0 0 1 0 1.4l-5 5a1 1 0 0 1-1.4-1.4l4.3-4.3-4.3-4.3a1 1 0 0 1 0-1.4Z'
        }
      />
    </svg>
  );
}
